import React, { useState, useEffect, useRef } from "react";
import TextareaAutosize from "react-textarea-autosize";
import "../styles/components/form.css";
import DeleteIcon from "../assets/icons/delete-white-icon.svg";
import CloseIcon from "../assets/icons/close-icon.svg";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  createDetailedNews,
  updateDetailedNews,
} from "../services/newsService";
import Loader from "../components/Loader";


import { IMAGE_BASE_URL } from "../services/imageConfig";

const getLocalDateString = () => {
  const now = new Date();
  const localISO = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString();
  return localISO.slice(0, 10);
};

const getTomorrowLocalDateString = () => {
  const now = new Date();
  const tomorrow = new Date(now.getTime() + 86400000);
  const localISO = new Date(tomorrow.getTime() - tomorrow.getTimezoneOffset() * 60000).toISOString();
  return localISO.slice(0, 10);
};

const DetailedNewsForm = ({ onClose, initialData, mode, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: initialData?.title || "",
    date: initialData?.date || (mode === "create" ? getLocalDateString() : ""),
    dateOfExpiry: initialData?.dateOfExpiry || (mode === "create" ? getTomorrowLocalDateString() : ""),
    description: initialData?.description || "",
    newsDetails: initialData?.newsDetails || "",
    titleImage: initialData?.titleImage || null,
    imageFirst: initialData?.imageFirst || null,
  });

  const MAX_FILE_SIZE_MB = 10;

  const [sliderPreview, setSliderPreview] = useState(null);
  const [detailPreview, setDetailPreview] = useState(null);

  const sliderInputRef = useRef(null);
  const detailInputRef = useRef(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!formData.titleImage) {
      setSliderPreview(null);
      return;
    }
    if (formData.titleImage instanceof File) {
      const objectUrl = URL.createObjectURL(formData.titleImage);
      setSliderPreview(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else {
      if (
        formData.titleImage.startsWith("http://") ||
        formData.titleImage.startsWith("https://")
      ) {
        setSliderPreview(formData.titleImage);
      } else {
        setSliderPreview(`${IMAGE_BASE_URL}${formData.titleImage}`);
      }
    }
  }, [formData.titleImage]);

  useEffect(() => {
    if (!formData.imageFirst) {
      setDetailPreview(null);
      return;
    }
    if (formData.imageFirst instanceof File) {
      const objectUrl = URL.createObjectURL(formData.imageFirst);
      setDetailPreview(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else {
      if (
        formData.imageFirst.startsWith("http://") ||
        formData.imageFirst.startsWith("https://")
      ) {
        setDetailPreview(formData.imageFirst);
      } else {
        setDetailPreview(`${IMAGE_BASE_URL}${formData.imageFirst}`);
      }
    }
  }, [formData.imageFirst]);

  const isValidFileSize = (file) => {
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      toast.error(`Файл "${file.name}" превышает максимальный размер 10MB.`, {
        position: "bottom-left",
        autoClose: 3000,
      });
      return false;
    }
    return true;
  };

  const handleSliderFileChange = (e) => {
    const file = e.target.files[0];
    if (file && isValidFileSize(file)) {
      setFormData((prev) => ({ ...prev, titleImage: file }));
    }
  };

  const handleSliderDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && isValidFileSize(file)) {
      setFormData((prev) => ({ ...prev, titleImage: file }));
    }
  };

  const handleSliderClick = () => sliderInputRef.current?.click();

  const removeSliderImage = () => {
    setFormData((prev) => ({ ...prev, titleImage: null }));
  };

  const handleDetailFileChange = (e) => {
    const file = e.target.files[0];
    if (file && isValidFileSize(file)) {
      setFormData((prev) => ({ ...prev, imageFirst: file }));
    }
  };

  const handleDetailDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && isValidFileSize(file)) {
      setFormData((prev) => ({ ...prev, imageFirst: file }));
    }
  };

  const handleDetailClick = () => detailInputRef.current?.click();

  const removeDetailImage = () => {
    setFormData((prev) => ({ ...prev, imageFirst: null }));
  };

  const isFormValid =
    formData.title.trim() !== "" &&
    formData.date.trim() !== "" &&
    formData.dateOfExpiry.trim() !== "" &&
    formData.description.trim() !== "" &&
    formData.newsDetails.trim() !== "" &&
    formData.titleImage;

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSubmitting(true);

    try {
      const submissionData = new FormData();
      submissionData.append(
        "data_client",
        JSON.stringify({
          title: formData.title,
          date: formData.date,
          dateOfExpiry: formData.dateOfExpiry,
          description: formData.description,
          newsDetails: formData.newsDetails,
        })
      );

      if (formData.titleImage instanceof File) {
        submissionData.append("image_article", formData.titleImage);
      } else {
        submissionData.append("image_article", "null");
      }

      if (formData.imageFirst instanceof File) {
        submissionData.append("image_first", formData.imageFirst);
      } else {
        submissionData.append("image_first", "null");
      }

      // Если imageFirst удалена (null), отправляем isDeleted=true
      const isImageDeleted = formData.imageFirst === null || formData.imageFirst === "";
      submissionData.append("isDeleted", isImageDeleted.toString());

      if (mode === "create") {
        await createDetailedNews(submissionData);
        toast.success("Новость успешно создана", {
          position: "bottom-left",
          autoClose: 3000,
        });
      } else {
        await updateDetailedNews(initialData.id, submissionData);
        toast.success("Новость успешно изменена", {
          position: "bottom-left",
          autoClose: 3000,
        });
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(`Ошибка: ${err.response?.data?.message || err.message}`, {
        position: "bottom-left",
        autoClose: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };


  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  return (
    <div className="modal-overlay">
      <div className="form">
        <div className="form-header">
          <h1 className="form-title">
            {mode === "edit"
              ? "Редактирование новости"
              : "Создание детальной новости"}
          </h1>
          <button className="close-button" onClick={onClose}>
            <img src={CloseIcon} alt="Закрыть" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <h2 className="form-section-title">Информация новости для слайдера</h2>

          <div className="form-group">
            <label className="field-label">Картинка для слайдера</label>
            <div
              className="drag-drop-area"
              onClick={handleSliderClick}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleSliderDrop}
            >
              {sliderPreview ? (
                <div className="image-preview">
                  <img
                    src={sliderPreview}
                    alt="Превью слайдера"
                    className="preview-image"
                  />
                  <button
                    type="button"
                    className="delete-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeSliderImage();
                    }}
                  >
                    <img src={DeleteIcon} alt="Удалить" />
                  </button>
                </div>
              ) : (
                <span className="placeholder-text">
                  Перетащите или кликните для загрузки картинки
                </span>
              )}
              <input
                type="file"
                accept="image/*"
                ref={sliderInputRef}
                onChange={handleSliderFileChange}
                hidden
              />
            </div>
            <label className="field-label max-size-label">Макс. размер: 10MB. Рекомендуемый размер: 1920×1080px</label>
          </div>

          <div className="form-group">
            <label className="field-label">Дата новости</label>
            <input
              type="date"
              className="date-input"
              value={formData.date}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, date: e.target.value }))
              }
            />
          </div>

          <div className="form-group">
            <label className="field-label">Дата окончания новости</label>
            <input
              type="date"
              className="date-input"
              value={formData.dateOfExpiry}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  dateOfExpiry: e.target.value,
                }))
              }
            />
          </div>

          <div className="form-group">
            <label className="field-label">Заголовок новости</label>
            <TextareaAutosize
              className="textarea-input"
              placeholder="Введите заголовок новости"
              value={formData.title}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, title: e.target.value }))
              }
            />
          </div>

          <div className="form-group">
            <label className="field-label">Описание новости</label>
            <TextareaAutosize
              className="textarea-input description"
              placeholder="Введите описание новости"
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
            />
          </div>

          <h2 className="form-section-title">Подробная информация новости</h2>

          <div className="form-group">
            <label className="field-label">Картинка для детальной информации (необязательно)</label>
            <div
              className="drag-drop-area"
              onClick={handleDetailClick}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDetailDrop}
            >
              {detailPreview ? (
                <div className="image-preview">
                  <img
                    src={detailPreview}
                    alt="Превью детали"
                    className="preview-image"
                  />
                  <button
                    type="button"
                    className="delete-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeDetailImage();
                    }}
                  >
                    <img src={DeleteIcon} alt="Удалить" />
                  </button>
                </div>
              ) : (
                <span className="placeholder-text">
                  Перетащите или кликните для загрузки картинки
                </span>
              )}
              <input
                type="file"
                accept="image/*"
                ref={detailInputRef}
                onChange={handleDetailFileChange}
                hidden
              />
            </div>
            <label className="field-label max-size-label">Максимальный размер для изображения: 10MB</label>
          </div>

          <div className="form-group">
            <label className="field-label">Подробности новости</label>
            <TextareaAutosize
              className="textarea-input description"
              placeholder="Введите подробности новости"
              value={formData.newsDetails}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, newsDetails: e.target.value }))
              }
            />
          </div>

          <div className="form-buttons">
            <button type="button" className="cancel-button" onClick={onClose}>
              Отменить
            </button>
            <button type="submit" className="save-button" disabled={!isFormValid}>
              Сохранить
            </button>
          </div>
        </form>
        {isSubmitting && (
          <div className="loader-overlay">
            <Loader />
            <p>Сохранение новости...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DetailedNewsForm;
